import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useFeedFirstPaintSettled } from "../utils/feedFirstPaintGate";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

/** Toggled by <RecaptchaGate> so the badge shows only on protected forms. */
export const RECAPTCHA_BADGE_VISIBLE_CLASS = "recaptcha-badge-visible";

const noopExecuteRecaptcha = () => Promise.resolve(null);

const RecaptchaContext = createContext({
  executeRecaptcha: noopExecuteRecaptcha,
  // True once a caller-triggered executeRecaptcha() will actually round-trip
  // to Google rather than resolve to a token-less no-op. Pages that auto-fire
  // on mount (e.g. Onboarding's verify-email autologin) need this to avoid
  // sending a request the BE will 400 for lacking a token; button-triggered
  // flows (Login/Signup submit) don't check it because the script has almost
  // always finished loading by the time a human fills out the form.
  isReady: true,
});

// Stable object identities — the library re-inits the script when these change.
const SCRIPT_PROPS = { async: true, defer: true, appendTo: "head" };
const CONTAINER = {
  parameters: {
    badge: "bottomright",
    theme: "dark",
  },
};

/**
 * Runs inside GoogleReCaptchaProvider and reports the library's executeRecaptcha
 * (undefined until the script + widget finish loading) up to our provider.
 */
function RecaptchaBridge({ onChange }) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  useEffect(() => {
    onChange(executeRecaptcha);
  }, [executeRecaptcha, onChange]);
  return null;
}

/**
 * Flag-on provider. Google's provider is a SIBLING of the app, not its parent,
 * so it can mount later without remounting the app tree.
 *
 * T22.201: on a Discover boot it mounts (and Google's script loads) only after
 * the first wheels have painted (utils/feedFirstPaintGate.js) -- MEASURED on
 * web-stg main.cb4edecd that the script's boot was ~540 ms of main-thread work
 * ahead of the first wheels on a 4x-throttled phone. Every other boot URL
 * mounts it on the first render, as before.
 */
function EnabledRecaptchaProvider({ children }) {
  const firstPaintSettled = useFeedFirstPaintSettled();
  // The latest library function lives in a ref and only readiness is state, so
  // a provider that hands out a new function per render cannot loop renders.
  const executeRef = useRef(undefined);
  const [isReady, setIsReady] = useState(false);
  const onChange = useCallback((fn) => {
    executeRef.current = fn;
    setIsReady(!!fn);
  }, []);
  const execute = useCallback((...args) => executeRef.current(...args), []);
  const value = useMemo(
    () => ({
      // Callers never need a null check; isReady is the true not-ready signal.
      executeRecaptcha: isReady ? execute : noopExecuteRecaptcha,
      isReady,
    }),
    [isReady, execute]
  );
  return (
    <RecaptchaContext.Provider value={value}>
      {children}
      {firstPaintSettled && (
        <GoogleReCaptchaProvider
          reCaptchaKey={RECAPTCHA_SITE_KEY}
          scriptProps={SCRIPT_PROPS}
          container={CONTAINER}
        >
          <RecaptchaBridge onChange={onChange} />
        </GoogleReCaptchaProvider>
      )}
    </RecaptchaContext.Provider>
  );
}

/**
 * App-root provider: loads Google reCAPTCHA v3 once for the whole app.
 * Mounting/unmounting the provider per-page causes "reCAPTCHA Timeout (b)".
 *
 * The floating badge is hidden by CSS on public pages; wrap protected forms in
 * `<RecaptchaGate>` to reveal it while keeping executeRecaptcha available.
 */
export function ConditionalRecaptchaProvider({ children }) {
  if (RECAPTCHA_ENABLED && RECAPTCHA_SITE_KEY) {
    return <EnabledRecaptchaProvider>{children}</EnabledRecaptchaProvider>;
  }

  return (
    <RecaptchaContext.Provider
      value={{ executeRecaptcha: noopExecuteRecaptcha, isReady: true }}
    >
      {children}
    </RecaptchaContext.Provider>
  );
}

// Refcount so nested gates / Strict Mode remounts don't flicker the badge.
let badgeVisibleCount = 0;

function syncBadgeVisibleClass() {
  if (typeof document === "undefined") return;
  document.body.classList.toggle(
    RECAPTCHA_BADGE_VISIBLE_CLASS,
    badgeVisibleCount > 0
  );
}

/**
 * Reveal the reCAPTCHA badge while this subtree is mounted (Login/Signup/
 * ForgotPassword, Settings Password, Contact Support). Does NOT mount another
 * Google provider — that must stay singleton at the app root.
 *
 * `enabled={false}` keeps the subtree mounted but leaves the badge hidden
 * (the caller must then show Google's inline attribution text instead).
 */
export function RecaptchaGate({ children, enabled = true }) {
  useEffect(() => {
    if (!enabled || !(RECAPTCHA_ENABLED && RECAPTCHA_SITE_KEY)) return undefined;
    badgeVisibleCount += 1;
    syncBadgeVisibleClass();
    return () => {
      badgeVisibleCount = Math.max(0, badgeVisibleCount - 1);
      syncBadgeVisibleClass();
    };
  }, [enabled]);

  return children;
}

export function useRecaptcha() {
  return useContext(RecaptchaContext);
}
